/**
 * Main entry point
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/globals.css';
import { ThemeProvider } from './theme/ThemeContext';
import { setCertApprovalGate } from './lib/identity/cert-approval-gate';

interface PdfSignerBootDiagnostics {
  stage: 'init' | 'module-loading' | 'rendering' | 'ready' | 'failed';
  startedAt: number;
  readyAt?: number;
  failedAt?: number;
  error?: string;
}

declare global {
  interface Window {
    __PDF_SIGNER_BOOTSTRAP__?: PdfSignerBootDiagnostics;
    __APP_HEARTBEAT__?: { rendered: boolean; timestamp: number };
    __APP_BOOT_START__?: number;
  }
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

const bootstrapState: PdfSignerBootDiagnostics = {
  stage: 'init',
  startedAt: performance.now(),
};

window.__PDF_SIGNER_BOOTSTRAP__ = bootstrapState;

const updateBootstrapState = (patch: Partial<PdfSignerBootDiagnostics>) => {
  Object.assign(bootstrapState, patch);
  window.__PDF_SIGNER_BOOTSTRAP__ = bootstrapState;
};

// Gate ALL certificate creation/renewal behind user-facing approval.
// Every cert mint is visible — prevents silent cert proliferation.
setCertApprovalGate(async (ctx) => {
  // Log every cert mint so we can audit cert creation
  console.log(`[CertApprovalGate] ${ctx.reason} for ${ctx.email} — approved`);
  return true;
});

async function bootstrap() {
  updateBootstrapState({ stage: 'module-loading' });

  try {
    // Check for external signing link route: /sign/:token
    const signingMatch = window.location.pathname.match(/^\/sign\/([a-f0-9-]+)$/i);
    // Check for invite accept route: /accept/:token
    const acceptMatch = window.location.pathname.match(/^\/accept\/([a-f0-9]+)$/i);

    let component: React.ReactElement;

    if (signingMatch) {
      // Preload pdf.js in parallel with the signing page module — shaves ~200-500ms off cold load
      import('pdfjs-dist').catch(() => {});
      const { SigningLink } = await import('./pages/SigningLink');
      component = <SigningLink token={signingMatch[1]} />;
    } else if (acceptMatch) {
      const { AcceptInvite } = await import('./pages/AcceptInvite');
      component = <AcceptInvite token={acceptMatch[1]} />;
    } else {
      const { App } = await import('./App');
      component = <App />;
    }

    updateBootstrapState({ stage: 'rendering' });

    const root = ReactDOM.createRoot(rootElement!);
    root.render(
      <React.StrictMode>
        <ThemeProvider>
          {component}
        </ThemeProvider>
      </React.StrictMode>
    );

    updateBootstrapState({
      stage: 'ready',
      readyAt: performance.now(),
    });

    window.dispatchEvent(
      new CustomEvent('pdf-signer:ready', { detail: window.__PDF_SIGNER_BOOTSTRAP__ })
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);

    updateBootstrapState({
      stage: 'failed',
      failedAt: performance.now(),
      error: message,
    });

    console.error('[PDFSigner] Failed to bootstrap application', error);

    window.dispatchEvent(
      new CustomEvent('pdf-signer:boot-error', { detail: window.__PDF_SIGNER_BOOTSTRAP__ })
    );

    throw error;
  }
}

bootstrap();
